import i18n from '../i18n/config';
/**
 * Format an entity (Company or Owner) with optional HubspotId display
 * @param entity - The entity to format (Company or Owner)
 * @param showHubspotId - Whether to show the hubspotId (typically isKasmAdmin)
 * @returns Formatted string: "Name (HubspotId)" or "Name" or "HubspotId" or "-"
 */
export const formatHubspotEntity = (entity, showHubspotId) => {
    if (!entity)
        return '-';
    const { name, hubspotId } = entity;
    const visibleHubspotId = showHubspotId ? hubspotId : '';
    return name && visibleHubspotId
        ? `${name} (${visibleHubspotId})`
        : name || visibleHubspotId || '-';
};
/**
 * Format a number according to the current locale
 * @param value - The number to format
 * @param options - Intl.NumberFormat options
 * @returns Formatted number string
 */
export const formatNumber = (value, options) => {
    return new Intl.NumberFormat(i18n.language, options).format(value);
};
/**
 * Format a date according to the current locale
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export const formatDate = (date, options) => {
    let dateObj;
    if (typeof date === 'string') {
        // Normalize datetime string
        let dateString = date.trim().replace(' ', 'T');
        if (!dateString.endsWith('Z')) {
            dateString = `${dateString}Z`;
        }
        dateObj = new Date(dateString);
    }
    else if (typeof date === 'number') {
        dateObj = new Date(date);
    }
    else {
        dateObj = date;
    }
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options,
    };
    return new Intl.DateTimeFormat(i18n.language, defaultOptions).format(dateObj);
};
/**
 * Format a date and time according to the current locale
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date and time string
 */
export const formatDateTime = (date, options) => {
    let dateObj;
    if (typeof date === 'string') {
        // Normalize datetime string
        let dateString = date.trim();
        dateString = dateString.replace(' ', 'T');
        if (!dateString.endsWith('Z')) {
            dateString = `${dateString}Z`;
        }
        dateObj = new Date(dateString);
    }
    else if (typeof date === 'number') {
        dateObj = new Date(date);
    }
    else {
        dateObj = date;
    }
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...options,
    };
    return new Intl.DateTimeFormat(i18n.language, defaultOptions).format(dateObj);
};
/**
 * Format a currency value according to the current locale
 * @param value - The amount to format
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD', options) => {
    return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
        ...options,
    }).format(value);
};
/**
 * Format a relative time (e.g., "2 days ago", "in 3 hours")
 * @param date - The date to compare with now
 * @param baseDate - The base date to compare against (defaults to now)
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (date, baseDate = new Date()) => {
    let dateObj;
    if (typeof date === 'string') {
        // Normalize datetime string
        let dateString = date.trim().replace(' ', 'T');
        if (!dateString.endsWith('Z')) {
            dateString = `${dateString}Z`;
        }
        dateObj = new Date(dateString);
    }
    else if (typeof date === 'number') {
        dateObj = new Date(date);
    }
    else {
        dateObj = date;
    }
    const diffInSeconds = Math.floor((dateObj.getTime() - baseDate.getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
    const units = [
        { unit: 'year', seconds: 31536000 },
        { unit: 'month', seconds: 2592000 },
        { unit: 'week', seconds: 604800 },
        { unit: 'day', seconds: 86400 },
        { unit: 'hour', seconds: 3600 },
        { unit: 'minute', seconds: 60 },
        { unit: 'second', seconds: 1 },
    ];
    for (const { unit, seconds } of units) {
        if (Math.abs(diffInSeconds) >= seconds) {
            const value = Math.floor(diffInSeconds / seconds);
            return rtf.format(value, unit);
        }
    }
    return rtf.format(0, 'second');
};
